import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { recordFeedback, recordFunderFeedback } from '../api'
import Button from '../components/Button'
import { ErrorAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'

/**
 * Feedback confirm page — target of the 👍/👎 links in digest emails:
 *   grants:      /feedback?client_id=...&grant_id=...&feedback=relevant|irrelevant
 *   foundations: /feedback?client_id=...&funder_id=...&feedback=relevant|irrelevant
 *
 * A confirm button (POST) rather than recording on page load: email scanners
 * prefetch every link in a digest via GET — both thumbs — which would write
 * garbage into the matching engine's feedback signal. Loading this page does
 * nothing; only the explicit click records.
 */
export default function Feedback() {
  useDocumentTitle('Feedback')
  const [params] = useSearchParams()
  const clientId = params.get('client_id')
  const grantId  = params.get('grant_id')
  const funderId = params.get('funder_id')
  const value    = params.get('feedback')

  const valid = clientId && (grantId || funderId) &&
    (value === 'relevant' || value === 'irrelevant')
  const isPositive = value === 'relevant'
  // Foundation leads and grant opportunities share this page; only the copy
  // and the POST target differ.
  const noun = funderId ? 'foundation' : 'grant'

  const [done, setDone]       = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError]     = useState('')
  const [busy, setBusy]       = useState(false)

  async function confirm() {
    setBusy(true)
    setError('')
    try {
      const res = funderId
        ? await recordFunderFeedback({
            client_id: clientId,
            funder_id: funderId,
            feedback: value,
          })
        : await recordFeedback({
            client_id: clientId,
            grant_id: grantId,
            feedback: value,
          })
      setMessage(res.message)
      setDone(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        {!valid ? (
          <>
            <div className="text-4xl mb-4">🤔</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid feedback link</h1>
            <p className="text-sm text-gray-500 mb-6">
              Please use the 👍 / 👎 links from a Praenum digest email, or give
              feedback directly on your dashboard.
            </p>
            <Link to="/dashboard" className="text-sm font-medium text-brand-500 hover:underline">
              Go to dashboard →
            </Link>
          </>
        ) : done ? (
          <>
            <div className="text-4xl mb-4">{isPositive ? '👍' : '👎'}</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Feedback recorded</h1>
            <p className="text-sm text-gray-500 mb-6">{message}</p>
            <Link to="/dashboard" className="text-sm font-medium text-brand-500 hover:underline">
              See all your matches →
            </Link>
          </>
        ) : (
          <>
            <div className="text-4xl mb-4">{isPositive ? '👍' : '👎'}</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {isPositive ? `Mark this ${noun} as relevant?` : `Mark this ${noun} as not for you?`}
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              {isPositive
                ? `We'll look for more ${funderId ? 'foundations' : 'opportunities'} like this one.`
                : `We'll show you fewer ${funderId ? 'foundations' : 'grants'} like this in future digests.`}
            </p>
            <ErrorAlert message={error} />
            <div className="flex flex-col gap-3 items-center mt-2">
              <Button onClick={confirm} loading={busy} variant="primary">
                {isPositive ? 'Yes, mark relevant' : 'Yes, not for us'}
              </Button>
              <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700">
                Cancel
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
